import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import { motion } from "framer-motion";
import {
  Copy,
  Check,
  Users,
  DollarSign,
  TrendingUp,
  Share2,
  ChevronDown,
  ChevronUp,
  Sparkles,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { useBrandName } from "../lib/company";
import { hapticFeedback, enableBackButton, telegram } from "../lib/telegram";
import { GradientHeader } from "../components/GradientHeader";
import { SectionCard } from "../components/SectionCard";
import { PageContainer } from "../components/PageContainer";
import {
  GENERATE_REFERRAL_CODE,
  GET_REFERRAL_DASHBOARD,
  GET_MY_REFERRALS,
  GET_MY_REFERRAL_LEDGER,
} from "../graphql/referral";

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);

const faqItems = [
  {
    q: "How does the referral program work?",
    a: "Share your unique link with friends. When they sign up and make crypto deposits, you earn a percentage of the company fee on every deposit they make.",
  },
  {
    q: "How many tiers are there?",
    a: "Up to 3 tiers. You earn from people you refer (Tier 1), people they refer (Tier 2), and one more level deep (Tier 3). Each tier has its own rate.",
  },
  {
    q: "When do I get paid?",
    a: "Referral earnings accrue in your balance automatically. Payouts are processed by your company admin and deposited to your selected payout card.",
  },
  {
    q: "Is there a minimum payout?",
    a: "Yes, your company sets a minimum redemption threshold. You'll be able to cash out once your balance reaches that amount.",
  },
  {
    q: "Do referral links expire?",
    a: "No. Your referral link is permanent and will always be linked to your account.",
  },
];

export const Referral = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const brandName = useBrandName();
  const [copied, setCopied] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  useEffect(() => {
    return enableBackButton(() => navigate("/"));
  }, [navigate]);

  const { data: dashData, loading: dashLoading, refetch } = useQuery(
    GET_REFERRAL_DASHBOARD,
    { skip: !user?.id }
  );
  const dashboard = dashData?.getReferralDashboard;

  const [generateCode, { loading: generating }] = useMutation(
    GENERATE_REFERRAL_CODE,
    { onCompleted: () => refetch() }
  );

  const { data: referralsData } = useQuery(GET_MY_REFERRALS, {
    variables: { userId: user?.id, limit: 10, offset: 0 },
    skip: !user?.id,
  });

  const { data: ledgerData } = useQuery(GET_MY_REFERRAL_LEDGER, {
    variables: { userId: user?.id, limit: 10, offset: 0 },
    skip: !user?.id,
  });

  const handleCopy = async () => {
    if (!dashboard?.referralLink) return;
    try {
      await navigator.clipboard.writeText(dashboard.referralLink);
      hapticFeedback.success();
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for TG WebView
      hapticFeedback.error();
    }
  };

  const handleShare = () => {
    if (!dashboard?.referralLink) return;
    hapticFeedback.medium();
    const text = `Join me on ${brandName}! Load crypto, get a virtual card, and start spending instantly.`;
    const url = `https://t.me/share/url?url=${encodeURIComponent(dashboard.referralLink)}&text=${encodeURIComponent(text)}`;
    telegram.openTelegramLink(url);
  };

  const handleGenerate = async () => {
    hapticFeedback.medium();
    await generateCode();
    hapticFeedback.success();
  };

  const referrals = referralsData?.users || [];
  const accruals = referralsData?.accruals || [];
  const ledger = ledgerData?.referral_balance_ledger || [];

  // Aggregate earnings per referred user
  const earningsPerUser: Record<string, number> = {};
  for (const entry of accruals) {
    if (entry.source_user_id) {
      earningsPerUser[entry.source_user_id] = (earningsPerUser[entry.source_user_id] || 0) + Number(entry.amount);
    }
  }
  const totalEarned = dashboard?.totalEarned || 0;
  const available = dashboard?.availableBalance || 0;
  const paidOut = dashboard?.totalPaidOut || 0;
  const referralsCount = dashboard?.referralsCount || 0;

  return (
    <PageContainer>
      {/* Header */}
      <GradientHeader size="lg">
        <p className="font-mono text-[11px] uppercase tracking-[0.12em] text-muted mb-1.5">
          Referral program
        </p>
        <h1 className="font-display text-[28px] leading-[1.05] font-semibold text-ink tracking-[-0.02em]">
          Invite friends.
          <br />
          Earn on every deposit.
        </h1>
      </GradientHeader>

      {/* Share Link Card */}
      <div className="px-4 mt-4">
        <div className="bg-surface rounded-xl border border-hairline overflow-hidden">
          {dashboard?.referralCode ? (
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.12em] text-muted">
                  Your referral link
                </p>
                <span className="stamp text-accent">{dashboard.referralCode}</span>
              </div>

              <div className="flex gap-2 mb-3">
                <div className="flex-1 bg-ground rounded-xl px-3 py-2.5 text-xs text-ink font-mono truncate border border-hairline">
                  {dashboard.referralLink}
                </div>
                <button
                  onClick={handleCopy}
                  className="w-10 h-10 flex items-center justify-center rounded-xl bg-ground text-accent active:scale-95 transition-transform border border-hairline"
                >
                  {copied ? <Check size={18} /> : <Copy size={18} />}
                </button>
              </div>

              <button
                onClick={handleShare}
                className="w-full flex items-center justify-center gap-2 bg-ink text-ground font-semibold py-3 rounded-xl active:scale-[0.98] transition-transform"
              >
                <Share2 size={18} />
                Share via Telegram
              </button>
            </div>
          ) : (
            <div className="p-5 text-center">
              <Sparkles size={32} className="text-accent mx-auto mb-3" />
              <p className="text-sm font-medium text-ink mb-1">
                Start earning with referrals
              </p>
              <p className="text-xs text-muted mb-4 leading-relaxed">
                Generate your unique link and earn a cut of every deposit your referrals make
              </p>
              <button
                onClick={handleGenerate}
                disabled={generating || dashLoading}
                className="w-full bg-ink text-ground font-semibold py-3 rounded-xl active:scale-[0.98] transition-transform disabled:opacity-60"
              >
                {generating ? "Generating…" : "Get my referral link"}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="px-4 mt-4 grid grid-cols-2 gap-3">
        {[
          { icon: DollarSign, label: "Available", value: formatCurrency(available), accent: true },
          { icon: TrendingUp, label: "Earned", value: formatCurrency(totalEarned) },
          { icon: Users, label: "Referrals", value: String(referralsCount) },
          { icon: DollarSign, label: "Paid out", value: formatCurrency(paidOut) },
        ].map((s) => (
          <div key={s.label} className="bg-surface rounded-xl border border-hairline p-3.5">
            <div className="flex items-center gap-2 mb-1.5">
              <s.icon size={14} className={s.accent ? "text-accent" : "text-muted"} />
              <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-muted font-semibold">
                {s.label}
              </span>
            </div>
            <p className="money text-xl font-semibold text-ink">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Recent Earnings */}
      {ledger.length > 0 && (
        <div className="px-4 mt-4">
          <SectionCard title="Recent earnings">
            <div className="space-y-0 divide-y divide-hairline">
              {ledger.map((entry: any) => (
                <div key={entry.id} className="flex items-center justify-between py-2.5 first:pt-0 last:pb-0">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full bg-ground border border-hairline flex items-center justify-center ${
                      entry.entry_type === "ACCRUAL"
                        ? "text-success"
                        : entry.entry_type === "PAYOUT"
                        ? "text-accent"
                        : "text-error"
                    }`}>
                      {entry.entry_type === "ACCRUAL" ? (
                        <TrendingUp size={14} />
                      ) : (
                        <DollarSign size={14} />
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-ink">
                        {entry.entry_type === "ACCRUAL"
                          ? `Tier ${entry.tier} credit`
                          : entry.entry_type === "PAYOUT"
                          ? "Payout"
                          : "Reversal"}
                      </p>
                      <p className="font-mono text-[11px] text-muted">
                        {entry.source_user?.name
                          ? `From ${entry.source_user.name}`
                          : new Date(entry.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <span className={`money text-sm font-semibold ${
                    entry.entry_type === "ACCRUAL" ? "text-success" : "text-ink"
                  }`}>
                    {entry.entry_type === "ACCRUAL" ? "+" : "-"}
                    {formatCurrency(entry.amount)}
                  </span>
                </div>
              ))}
            </div>
          </SectionCard>
        </div>
      )}

      {/* My Referrals */}
      <div className="px-4 mt-4">
        <SectionCard title={`My referrals (${referralsCount})`}>
          {referrals.length > 0 ? (
            <div className="space-y-0 divide-y divide-hairline">
              {referrals.map((ref: any) => (
                <div key={ref.id} className="flex items-center justify-between py-2.5 first:pt-0 last:pb-0">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-ground border border-hairline flex items-center justify-center">
                      <span className="text-xs font-bold text-ink">
                        {(ref.name || ref.email || "?").charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-ink">
                        {ref.name || ref.email || "User"}
                      </p>
                      <p className="font-mono text-[11px] text-muted">
                        {ref.telegram_id
                          ? `TG #${ref.telegram_id}`
                          : `Joined ${new Date(ref.created_at).toLocaleDateString()}`}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    {earningsPerUser[ref.id] > 0 && (
                      <p className="money text-sm font-semibold text-success">
                        +{formatCurrency(earningsPerUser[ref.id])}
                      </p>
                    )}
                    <span className={`stamp ${ref.is_active ? "text-success" : "text-muted"}`}>
                      {ref.is_active ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center text-center py-4">
              <div className="w-10 h-10 rounded-xl bg-ground border border-hairline flex items-center justify-center mb-2">
                <Users size={18} className="text-muted/60" />
              </div>
              <p className="text-sm text-muted">No referrals yet</p>
              <p className="text-xs text-muted/70 mt-0.5">
                Share your link to start earning
              </p>
            </div>
          )}
        </SectionCard>
      </div>

      {/* How It Works / FAQ */}
      <div className="px-4 mt-4">
        <SectionCard title="How it works">
          {/* Quick overview steps */}
          <div className="space-y-3 mb-4">
            {[
              { step: "1", text: "Share your unique referral link with friends" },
              { step: "2", text: `They sign up and deposit crypto into ${brandName}` },
              { step: "3", text: "You earn a percentage of the fee on each deposit" },
            ].map((item) => (
              <div key={item.step} className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-ink text-ground font-mono text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                  {item.step}
                </div>
                <p className="text-sm text-ink/80 leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>

          <div className="h-px bg-hairline my-3" />

          {/* FAQ Accordion */}
          <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.12em] text-muted mb-2">
            FAQ
          </p>
          <div className="space-y-0 divide-y divide-hairline">
            {faqItems.map((item, idx) => (
              <div key={idx}>
                <button
                  onClick={() => {
                    hapticFeedback.selectionChanged();
                    setExpandedFaq(expandedFaq === idx ? null : idx);
                  }}
                  className="w-full flex items-center justify-between py-2.5 text-left"
                >
                  <span className="text-sm font-medium text-ink pr-4">{item.q}</span>
                  {expandedFaq === idx ? (
                    <ChevronUp size={16} className="text-muted shrink-0" />
                  ) : (
                    <ChevronDown size={16} className="text-muted shrink-0" />
                  )}
                </button>
                {expandedFaq === idx && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="pb-3"
                  >
                    <p className="text-xs text-muted leading-relaxed pl-0.5">
                      {item.a}
                    </p>
                  </motion.div>
                )}
              </div>
            ))}
          </div>
        </SectionCard>
      </div>

      {/* Bottom spacer */}
      <div className="h-4" />
    </PageContainer>
  );
};
